import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import McpTerminalApprovalModal from './McpTerminalApprovalModal';
import McpSessionGrantPill from './McpSessionGrantPill';
import { isTauri } from '../utils/tauri';
import { useTerminalExtStore } from '../store/terminalExtStore';
import { setActiveTab } from '../store/uiModalStore';

/**
 * Always-mounted host for MCP terminal command approvals.
 *
 * **Mounted at the app root, and that is the single most likely way this
 * feature ships broken.** `BridgePairHost.js` states the failure in its own
 * words: a panel-scoped listener means the event *"fires with nothing listening
 * and no window shows"* while the far side waits. Here the far side is an MCP
 * client that may be running unattended — the request can arrive while the user
 * is on ANY panel, or with the window hidden in the tray, so the listener
 * cannot live inside a settings page or a terminal panel. Rust raises and
 * focuses the window itself, and posts an OS notification, but neither helps if
 * nothing is subscribed to paint the card.
 *
 * `listenSafe` rather than a raw `listen()`: `PasskeyApprovalHost.js` learned
 * that a raw listener double-registers under StrictMode's dev double-mount and
 * paints two modals for one ceremony — two Approve buttons, one of which
 * resolves to "not found".
 *
 * **Single-flight**, like the passkey ceremony (`bridge.rs`'s `single_flight`,
 * and Rust's own `Approvals` registry): only one command can be waiting, so one
 * slot of state is enough. A queue of approval modals would be an
 * approval-fatigue attack rather than a feature.
 *
 * **This host never decides anything.** It renders what Rust sent and posts the
 * verdict back; the state machine, the 120 s refusal and the audit line all
 * live on the Rust side, so a webview that never loads, crashes or is closed
 * still ends in a refusal.
 */
export default function McpTerminalApprovalHost() {
  const { t } = useTranslation();
  const [request, setRequest] = useState(null);
  // The last target we showed a card for. `mcp:terminal_ai_session` carries
  // only ids, and the card's own state is cleared the moment the user decides,
  // so without this the tab would be labelled "AI" instead of the server's name.
  const lastTargetRef = useRef(null);
  // The live session grants (O42 as amended; one per server since 14.09.2026). Held here rather than in
  // the settings page because this host is the one thing mounted on every
  // panel: a grant the user can only find by opening Settings is not the
  // "visible and revocable" the Rust header promises.
  const [grants, setGrants] = useState([]);

  const refreshGrant = useCallback(async () => {
    if (!isTauri()) return;
    try {
      // A stopped server answers `null` here rather than throwing — `cancel_all`
      // has already revoked the grant — so the ordinary "it is gone" path runs
      // through the success branch.
      const next = await invoke('mcp_terminal_session_grant');
      setGrants(Array.isArray(next) ? next : next ? [next] : []);
    } catch (e) {
      // **The previous value is KEPT, not cleared.** Of the two ways to be
      // wrong, showing a pill for a grant that has ended costs one pointless
      // click; hiding one that is still live tells the user they are being
      // asked about every command when they are not. Only one of those is a
      // safety property.
      console.warn('[mcp] could not read the session grant:', e);
    }
  }, []);

  // `connId` ends that one server's grant; `null` ends every grant.
  const revokeGrant = useCallback(async (connId = null) => {
    if (!isTauri()) return;
    try {
      // **Awaited before the pill goes**, deliberately not optimistic. A
      // disappearing pill means "you are being asked again", and showing that
      // before it is true is exactly the lie this control exists to prevent.
      // The cost of being honest is a button that takes a few milliseconds.
      await invoke('mcp_terminal_revoke_session_grant', { connId });
      setGrants((prev) => (connId ? prev.filter((g) => g.connId !== connId) : []));
    } catch (e) {
      console.warn('[mcp] revoking the session grant failed:', e);
      // Leave the pill up: the grant may well still be in force, and a
      // refresh says so either way.
      refreshGrant();
    }
  }, [refreshGrant]);

  useEffect(() => {
    if (!isTauri()) return undefined;
    let disposed = false;
    const unlistens = [];
    (async () => {
      try {
        const { listenSafe } = await import('../utils/safeTauriEvent');
        const subscribe = async (name, handler) => {
          const unlisten = await listenSafe(name, handler);
          if (disposed) unlisten();
          else unlistens.push(unlisten);
        };
        await subscribe('mcp:terminal_approval', (event) => {
          const payload = event.payload || {};
          if (!payload.approvalId) return;
          lastTargetRef.current = { connId: payload.connId, connName: payload.connName };
          setRequest(payload);
        });
        // Rust opened (or reused) the tab an approved command is about to run
        // in. Bind it and bring it on screen — a command running in a panel the
        // user is not looking at is the thing this whole mode exists to fix.
        //
        // Rust sends this BEFORE the command, and the session already exists,
        // so the tab adopts `termId` rather than opening a second session.
        await subscribe('mcp:terminal_ai_session', (event) => {
          const { connId, termId } = event.payload || {};
          if (!connId || !termId) return;
          const last = lastTargetRef.current;
          const label = last && last.connId === connId ? last.connName : null;
          useTerminalExtStore.getState().openAiTab(connId, termId, label);
          setActiveTab('terminal.panel');
        });
        // A live session grant let a command through with no card. There is
        // nothing to answer, so this raises no modal and does NOT steal focus —
        // it binds the tab exactly as an approval would, so the command is
        // still watched even though it was not clicked. Rust forces
        // `visible = true` for these, so a tab always exists to bind.
        await subscribe('mcp:terminal_auto_approved', (event) => {
          const { connId, connName } = event.payload || {};
          if (!connId) return;
          // Each pass re-reads the budget, so the pill counts down as the grant
          // is spent and disappears of its own accord when it runs out.
          refreshGrant();
          // Keep the label fresh so the tab this command opens is named after
          // the server rather than falling back to the last card's target.
          lastTargetRef.current = { connId, connName };
        });
        await subscribe('mcp:terminal_approval_cancelled', (event) => {
          const { approvalId } = event.payload || {};
          if (!approvalId) return;
          // "for that approvalId only" — a stale cancel must never close the
          // card a newer proposal just opened.
          setRequest((current) =>
            current && current.approvalId === approvalId ? null : current
          );
        });
      } catch (e) {
        console.warn('[mcp] terminal approval listener setup failed:', e);
      }
    })();
    return () => {
      disposed = true;
      for (const fn of unlistens) fn();
      unlistens.length = 0;
    };
    // `refreshGrant` is a `[]`-dep callback, so this stays a mount-once effect
    // — it is listed because exhaustive-deps is an error in this build, not
    // because the subscriptions should ever be torn down and rebuilt.
  }, [refreshGrant]);

  // The time bound expires with no event to announce it, so the pill has to
  // ask. Only while one is actually in force — no grant, no polling.
  useEffect(() => {
    if (grants.length === 0) return undefined;
    const id = setInterval(refreshGrant, 15000);
    return () => clearInterval(id);
  }, [grants.length, refreshGrant]);

  const onRespond = useCallback((verdict) => {
    // Close first: the verdict is recorded synchronously on the Rust side and
    // the command then runs on its own worker thread. Waiting on the invoke
    // would leave the card up over work the user has already decided.
    setRequest(null);
    if (!isTauri()) return;
    invoke('mcp_terminal_respond', {
      approvalId: verdict.approvalId,
      approve: !!verdict.approve,
      // Rule 7: chosen on the card, at approve time. Rust decides nothing here
      // and the agent cannot ask for either mode; on a refusal it is ignored.
      visible: !!verdict.visible,
      // Rule 6 as amended (O42, 12.09.2026). Same provenance: a human ticked
      // it on the card that was in front of them. Rust ignores it on a refusal.
      grantSession: !!verdict.grantSession,
    }).catch((e) => {
      // Swept, cancelled or already answered. **A delivery failure can only
      // ever cost an approval, never grant one** — Rust's default at every
      // terminal state is refuse — so there is nothing to surface here.
      console.warn('[mcp] terminal verdict delivery failed:', e);
    });
    if (verdict.approve && verdict.grantSession) refreshGrant();
  }, [refreshGrant]);

  return (
    <>
      <McpTerminalApprovalModal request={request} onRespond={onRespond} t={t} />
      <McpSessionGrantPill grants={grants} onRevoke={revokeGrant} t={t} />
    </>
  );
}
